import React from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import { WireGuardTunnel } from "./wireGuardTunnelsInterfaces";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import {
  ComputedTunnelStatus,
  getComputedTunnelStatus,
} from "./wireGuardTunnelStatusUtils";

type SortDirection = "asc" | "desc";
const TABLE_MIN_WIDTH = 1330;

const getStatusStyles = (status: ComputedTunnelStatus) => {
  if (status === "Up") {
    return {
      backgroundColor: "#e6f4ea",
      color: "#1e7d34",
      dotColor: "#1e7d34",
    };
  }
  if (status === "Down") {
    return {
      backgroundColor: "#fdecec",
      color: "#b3261e",
      dotColor: "#b3261e",
    };
  }
  return {
    backgroundColor: "#eceff3",
    color: "#5f6368",
    dotColor: "#5f6368",
  };
};

interface Props {
  rows: WireGuardTunnel[];
  searchText: string;
  onSearchTextChange: (value: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (_: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRowClick: (row: WireGuardTunnel) => void;
  getGroupName: (groupId: number | null) => string;
  getServerName: (serverId: number | null) => string;
  onDeleteSelectedRows: (selectedIds: number[]) => Promise<void>;
  onDownloadCsvTemplate: () => void;
  isDownloadingTemplate: boolean;
  onBulkUploadClick: () => void;
  onExportCsv: () => void;
  isExportingCsv: boolean;
  onAddTunnelClick: () => void;
  /**
   * ENG-278: false hides every control that writes tunnels (add, bulk upload,
   * delete) and the CSV endpoints, which share the same API permission. The list
   * itself stays readable.
   */
  canCreateTunnels: boolean;
}

type SortKey =
  | "name"
  | "group"
  | "server"
  | "client_public_key"
  | "client_tunnel_ip"
  | "subnet"
  | "tunnel_status"

const headCells: Array<{ id: SortKey; label: string }> = [
  { id: "name", label: "Tunnel Name" },
  { id: "group", label: "Group" },
  { id: "server", label: "WireGuard Server" },
  { id: "client_public_key", label: "Public Key" },
  { id: "client_tunnel_ip", label: "Tunnel IP" },
  { id: "subnet", label: "Subnet" },
  { id: "tunnel_status", label: "Status" },
];

export const WireGuardTunnelsTable = ({
  rows,
  searchText,
  onSearchTextChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  getGroupName,
  getServerName,
  onDeleteSelectedRows,
  onDownloadCsvTemplate,
  isDownloadingTemplate,
  onBulkUploadClick,
  onExportCsv,
  isExportingCsv,
  onAddTunnelClick,
  canCreateTunnels,
}: Props) => {
  const [orderBy, setOrderBy] = React.useState<SortKey>("name");
  const [order, setOrder] = React.useState<SortDirection>("asc");
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [isDeletingSelected, setIsDeletingSelected] = React.useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = React.useState(false);

  const sortedRows = React.useMemo(() => {
    const sorted = [...rows];
    sorted.sort((first, second) => {
      let firstValue: string | number = "";
      let secondValue: string | number = "";

      if (orderBy === "group") {
        firstValue = getGroupName(first.group);
        secondValue = getGroupName(second.group);
      } else if (orderBy === "server") {
        firstValue = getServerName(first.server);
        secondValue = getServerName(second.server);
      } else if (orderBy === "subnet") {
        firstValue = first.subnets?.join(", ") ?? "";
        secondValue = second.subnets?.join(", ") ?? "";
      } else if (orderBy === "tunnel_status") {
        firstValue = getComputedTunnelStatus(first.last_handshake);
        secondValue = getComputedTunnelStatus(second.last_handshake);
      } else {
        firstValue = (first[orderBy] ?? "") as string | number;
        secondValue = (second[orderBy] ?? "") as string | number;
      }

      if (typeof firstValue === "number" && typeof secondValue === "number") {
        return order === "asc"
          ? firstValue - secondValue
          : secondValue - firstValue;
      }

      const compareValue = String(firstValue).localeCompare(String(secondValue));
      return order === "asc" ? compareValue : -compareValue;
    });
    return sorted;
  }, [getGroupName, getServerName, order, orderBy, rows]);

  const visibleRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const requestSort = (property: SortKey) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  React.useEffect(() => {
    const idsInRows = new Set(rows.map((row) => row.id));
    setSelectedIds((prev) => prev.filter((id) => idsInRows.has(id)));
  }, [rows]);

  const allVisibleSelected =
    sortedRows.length > 0 && sortedRows.every((row) => selectedIds.includes(row.id));

  const selectedCount = selectedIds.length;

  return (
    <Paper
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          className="relative"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 4,
            background: "white",
          }}
        >
          <div className="px-4 pt-[10px] pb-[10px]">
            <div className="flex items-center gap-3">
              <TextField
                size="small"
                placeholder="Filter by name, IP, subnet, server, group..."
                value={searchText}
                onChange={(event) => onSearchTextChange(event.target.value)}
                sx={{
                  width: "33%",
                  minWidth: 340,
                  maxWidth: 520,
                  flexShrink: 0,
                  backgroundColor: "#e9edf2",
                  "& *": { fontFamily: "Mulish" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                {canCreateTunnels && selectedCount > 0 && (
                  <OrangeTextButton
                    onClick={() => setBulkDeleteConfirmOpen(true)}
                    text={isDeletingSelected ? "Deleting..." : "Delete Tunnel"}
                    disabled={isDeletingSelected}
                  />
                )}
                {canCreateTunnels && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={onDownloadCsvTemplate}
                      disabled={isDownloadingTemplate}
                      startIcon={<DescriptionIcon />}
                      sx={{ fontFamily: "Mulish", textTransform: "none" }}
                    >
                      {isDownloadingTemplate ? "Downloading..." : "CSV TEMPLATE"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={onBulkUploadClick}
                      startIcon={<FileUploadIcon />}
                      sx={{ fontFamily: "Mulish", textTransform: "none" }}
                    >
                      BULK UPLOAD
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={onExportCsv}
                      disabled={isExportingCsv}
                      startIcon={<DownloadIcon />}
                      sx={{ fontFamily: "Mulish", textTransform: "none" }}
                    >
                      {isExportingCsv ? "Exporting..." : "EXPORT CSV"}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={onAddTunnelClick}
                      sx={{
                        fontFamily: "Mulish",
                        textTransform: "none",
                        backgroundColor: "#FF8400",
                        "&:hover": { backgroundColor: "#E67300" },
                      }}
                    >
                      + ADD TUNNEL
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <Table
          sx={{
            tableLayout: "fixed",
            width: TABLE_MIN_WIDTH,
            minWidth: TABLE_MIN_WIDTH,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <TableHead
            sx={{
              display: "table",
              width: TABLE_MIN_WIDTH,
              tableLayout: "fixed",
            }}
          >
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 56 }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={selectedCount > 0 && !allVisibleSelected}
                  onChange={(event) => {
                    if (event.target.checked) {
                      setSelectedIds(sortedRows.map((row) => row.id));
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                  inputProps={{ "aria-label": "select all tunnels" }}
                />
              </TableCell>
              {headCells.map((cell) => (
                <TableCell
                  key={cell.id}
                  sx={{ fontFamily: "Mulish", fontWeight: 700 }}
                >
                  <TableSortLabel
                    active={orderBy === cell.id}
                    direction={orderBy === cell.id ? order : "asc"}
                    onClick={() => requestSort(cell.id)}
                  >
                    <span style={{ fontFamily: "Mulish" }}>{cell.label}</span>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              display: "block",
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
              width: TABLE_MIN_WIDTH,
            }}
          >
            {visibleRows.map((row) => {
              const computedStatus = getComputedTunnelStatus(row.last_handshake);
              const statusStyles = getStatusStyles(computedStatus);

              return (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => onRowClick(row)}
                  sx={{
                    display: "table",
                    tableLayout: "fixed",
                    width: TABLE_MIN_WIDTH,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#e3ecfa !important",
                      "& .MuiTableCell-root": {
                        backgroundColor: "#e3ecfa !important",
                      },
                    },
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(row.id)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setSelectedIds((prev) =>
                          checked
                            ? [...prev, row.id]
                            : prev.filter((id) => id !== row.id)
                        );
                      }}
                      inputProps={{ "aria-label": `select tunnel ${row.name}` }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>{row.name}</TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {getGroupName(row.group)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {getServerName(row.server)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    <span
                      title={row.client_public_key || ""}
                      style={{
                        display: "block",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        lineHeight: 1.25,
                      }}
                    >
                      {row.client_public_key || "-"}
                    </span>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {row.client_tunnel_ip || "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    {row.subnets?.length ? row.subnets.join(", ") : "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Mulish" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        borderRadius: 999,
                        padding: "3px 10px",
                        fontFamily: "Mulish",
                        fontWeight: 600,
                        fontSize: 12,
                        backgroundColor: statusStyles.backgroundColor,
                        color: statusStyles.color,
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: statusStyles.dotColor,
                          display: "inline-block",
                        }}
                      />
                      {computedStatus}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  sx={{
                    display: "table-cell",
                    fontFamily: "Mulish",
                    py: 4,
                    width: "100%",
                  }}
                >
                  No tunnels match the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={sortedRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{ borderTop: "1px solid #d1d5db" }}
      />
      <Dialog
        open={bulkDeleteConfirmOpen}
        onClose={() => setBulkDeleteConfirmOpen(false)}
        PaperProps={{ sx: { "& *": { fontFamily: "Mulish" } } }}
      >
        <DialogTitle>Delete Selected Tunnels?</DialogTitle>
        <DialogContent>
          <span style={{ fontFamily: "Mulish" }}>
            Are you sure you want to delete {selectedCount} selected{" "}
            {selectedCount === 1 ? "tunnel" : "tunnels"}?
          </span>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkDeleteConfirmOpen(false)}
            disabled={isDeletingSelected}
            sx={{ fontFamily: "Mulish", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (isDeletingSelected) {
                return;
              }
              setIsDeletingSelected(true);
              try {
                await onDeleteSelectedRows(selectedIds);
                setSelectedIds([]);
                setBulkDeleteConfirmOpen(false);
              } finally {
                setIsDeletingSelected(false);
              }
            }}
            disabled={isDeletingSelected}
            sx={{
              fontFamily: "Mulish",
              textTransform: "none",
              color: "#d32f2f",
            }}
          >
            {isDeletingSelected ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
